import type {
  ConversationListItemResponse,
  ConversationMessageEntity,
} from '../../types/conversation.types';
import { minutesAgo, mockMessage } from './mockMessages';

/**
 * The conversations the admin mock opens with, one of each situation the
 * Dispatcher tab has to draw: a late delivery the AI handed to a person, a
 * thread the AI is still answering, one a colleague already answered (with a
 * team note), and a fresh question nobody has seen yet.
 */

export type MockThread = {
  row: ConversationListItemResponse;
  /** Oldest first. */
  messages: ConversationMessageEntity[];
};

let nextMessageId = 950000000;
export const mockMessageId = () => ++nextMessageId;

const AI = { id: null, name: 'vDrive Assistant', role: null };
const ANA = { id: 990001, name: 'Ana Petrovic', role: 'support' as never };
const customer = (name: string) => ({ id: null, name, role: null });

const row = (
  fields: Partial<ConversationListItemResponse> &
    Pick<ConversationListItemResponse, 'id' | 'name'>
): ConversationListItemResponse => ({
  type: 'dispatcher',
  imageUrl: null,
  supportUserId: null,
  supportUserRole: null,
  supportUserPhoneNumber: null,
  supportAudience: null,
  locations: null,
  participants: null,
  participantCount: 0,
  lastMessageAt: null,
  lastMessagePreview: null,
  lastMessageSide: null,
  lastMessageIsAssistant: false,
  unread: 0,
  hasMention: false,
  unansweredSince: null,
  archived: false,
  mutedUntil: null,
  dispatch: null,
  ...fields,
});

type Line = Partial<ConversationMessageEntity> & { at: number };

/** A thread's messages from short descriptions, `at` in minutes ago. */
const lines = (conversationId: number, list: Line[]) =>
  list.map(({ at, ...fields }) =>
    mockMessage({
      id: mockMessageId(),
      conversationId,
      side: 'internal',
      createdAt: minutesAgo(at),
      seen: true,
      ...fields,
    })
  );

const dispatch = (
  fields: Partial<NonNullable<ConversationListItemResponse['dispatch']>>
): NonNullable<ConversationListItemResponse['dispatch']> => ({
  deliveryId: 31877,
  trackingUid: 'COduvrsfB5',
  orderId: null,
  deliveryStatus: 'en_route_dropoff',
  provider: 'doordash',
  brandName: null,
  brandImageUrl: null,
  customerName: null,
  customerPhone: null,
  late: false,
  ...fields,
});

const threads = (): MockThread[] => [
  {
    row: row({
      id: 900001,
      name: 'Jessica Moore',
      locations: 'Long Island Bagel Cafe - Queens',
      unansweredSince: minutesAgo(2),
      unread: 2,
      dispatch: dispatch({
        orderId: 48213,
        brandName: 'Long Island Bagel Cafe',
        customerName: 'Jessica Moore',
        customerPhone: '(718) 555-0142',
        late: true,
      }),
    }),
    messages: lines(900001, [
      {
        at: 14,
        side: 'user',
        sender: customer('Jessica Moore'),
        body: 'Where is my food? I ordered almost an hour ago.',
      },
      {
        at: 13,
        isAssistant: true,
        sender: AI,
        body: 'Hi, I am the vDrive assistant, an AI. Your driver picked up your order 9 minutes ago and is about 12 minutes away. The time on the map updates live as they move.',
      },
      {
        at: 3,
        side: 'user',
        sender: customer('Jessica Moore'),
        body: 'This is ridiculous. The food will be cold. I want a refund.',
      },
      {
        at: 2,
        isAssistant: true,
        sender: AI,
        body: 'I understand. I am bringing in someone from the vDrive team who can help with that. They can see this whole chat.',
      },
      {
        at: 2,
        side: 'system',
        audience: 'internal',
        systemEvent: 'handedToHuman',
        systemMeta: {
          by: 'ai',
          reason: 'Customer asks for a refund after a 40 minute delay.',
        },
      },
    ]),
  },
  {
    row: row({
      id: 900004,
      name: 'Customer #48231',
      locations: 'Burger Barn - Astoria',
      unread: 1,
      dispatch: dispatch({
        deliveryId: 31890,
        trackingUid: 'K2p9sXq1Lm',
        orderId: 48231,
        deliveryStatus: 'runner_assigned',
        provider: 'relay',
        brandName: 'Burger Barn',
        customerPhone: '(347) 555-0108',
      }),
    }),
    messages: lines(900004, [
      {
        at: 1,
        side: 'user',
        sender: customer('Customer'),
        body: 'Is my driver lost? The map shows him going the other way.',
      },
    ]),
  },
  {
    row: row({
      id: 900002,
      name: 'Marcus Chen',
      locations: 'Sakura Sushi - Midtown',
      dispatch: dispatch({
        deliveryId: 31861,
        trackingUid: 'Qw3eRt5Yu7',
        orderId: 48190,
        deliveryStatus: 'picked_up',
        provider: 'uber',
        brandName: 'Sakura Sushi',
        customerName: 'Marcus Chen',
        customerPhone: '(917) 555-0175',
      }),
    }),
    messages: lines(900002, [
      {
        at: 22,
        side: 'user',
        sender: customer('Marcus Chen'),
        body: 'How long until it gets here?',
      },
      {
        at: 21,
        isAssistant: true,
        sender: AI,
        body: 'Hi, I am the vDrive assistant, an AI. Your driver has your order and should reach you in about 15 minutes.',
      },
    ]),
  },
  {
    row: row({
      id: 900003,
      name: 'Priya Patel',
      locations: 'Taco Loco - Brooklyn',
      dispatch: dispatch({
        deliveryId: 31802,
        trackingUid: 'Zx8cVb6Nm4',
        orderId: 48122,
        deliveryStatus: 'completed',
        provider: 'vdriveFleet',
        brandName: 'Taco Loco',
        customerName: 'Priya Patel',
        customerPhone: '(646) 555-0133',
      }),
    }),
    messages: lines(900003, [
      {
        at: 95,
        side: 'user',
        sender: customer('Priya Patel'),
        body: 'My horchata is missing from the bag.',
      },
      {
        at: 94,
        isAssistant: true,
        sender: AI,
        body: 'Hi, I am the vDrive assistant, an AI. I am sorry about that. Missing items are handled by Taco Loco: call them at (212) 555-0190 and they can fix it.',
      },
      {
        at: 82,
        sender: ANA,
        body: 'Hi Priya, this is Ana from vDrive. I called Taco Loco, they are refunding the drink to your card.',
      },
      {
        at: 80,
        sender: ANA,
        audience: 'internal',
        body: 'Refund confirmed by the store manager, nothing left to do.',
      },
    ]),
  },
];

/** Fresh on every page load: the mock keeps no state across reloads. */
export const seedAdminThreads = (): MockThread[] =>
  threads().map((thread) => {
    const last = thread.messages
      .filter((message) => message.audience !== 'internal')
      .slice(-1)[0];
    return {
      ...thread,
      row: {
        ...thread.row,
        lastMessageAt: last?.createdAt ?? null,
        lastMessagePreview: last?.body ?? null,
        lastMessageSide: last?.side ?? null,
        lastMessageIsAssistant: !!last?.isAssistant,
      },
    };
  });
